import { useRef, useEffect, useCallback } from 'react';
import { FiArrowUp } from "react-icons/fi";
import { LuHistory } from "react-icons/lu";
import MatchmakingHistoryPopover from '../MatchmakingHistoryPopover';
import { isGenericSearch } from '../../constants/client-selection';

type MatchmakingSearchInputProps = {
  matchingPrompt: string;
  onPromptChange: (value: string) => void;
  onGenerateMatches: () => void;
  isLoading: boolean;
  hasClients: boolean;
  isHistoryPopoverOpen: boolean;
  setHistoryPopoverOpen: (open: boolean) => void;
  showHistoryTooltip: boolean;
  setShowHistoryTooltip: (show: boolean) => void;
  onSelectHistory: (historyItem: any) => void;
  advisorId: string | null;
  clientId: string | null;
  shouldFetchHistory: boolean;
  onFetchHistoryComplete: () => void;
};

export const MatchmakingSearchInput = ({
  matchingPrompt,
  onPromptChange,
  onGenerateMatches,
  isLoading,
  hasClients,
  isHistoryPopoverOpen,
  setHistoryPopoverOpen,
  showHistoryTooltip,
  setShowHistoryTooltip,
  onSelectHistory,
  advisorId,
  clientId,
  shouldFetchHistory,
  onFetchHistoryComplete,
}: MatchmakingSearchInputProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isGeneric = isGenericSearch(clientId);
  const canSearch = isGeneric || hasClients;

  // Feature 039: Track cursor position to prevent jump bug
  const cursorPositionRef = useRef<number | null>(null);

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const lineHeight = 22;
    const paddingTop = 24;
    const paddingBottom = 24;
    const minHeight = lineHeight + paddingTop + paddingBottom;
    const maxHeight = lineHeight * 5 + paddingTop + paddingBottom;
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight}px`;
    if (scrollHeight > maxHeight) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [matchingPrompt, adjustTextareaHeight]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [adjustTextareaHeight]);

  // Feature 039: Restore cursor position after prompt changes
  useEffect(() => {
    if (cursorPositionRef.current !== null && textareaRef.current) {
      const position = cursorPositionRef.current;
      textareaRef.current.setSelectionRange(position, position);
      cursorPositionRef.current = null;
    }
  }, [matchingPrompt]);

  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 flex justify-center">
        <label className="sr-only" htmlFor="matchmaking-search">
          Describe the care needs
        </label>
        <div className="relative max-w-[70rem] w-full">
          <div className="relative w-full rounded-[35px] border border-[#dbdbdb] focus-within:border-[#026857] focus-within:ring-4 focus-within:ring-[#026857]/20 bg-white">
            <textarea
              ref={textareaRef}
              id="matchmaking-search"
              value={matchingPrompt}
              onChange={(event) => {
                const textarea = event.target;
                cursorPositionRef.current = textarea.selectionStart;
                onPromptChange(event.target.value);
                setTimeout(() => adjustTextareaHeight(), 0);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  if (matchingPrompt.trim() && !isLoading && canSearch) {
                    onGenerateMatches();
                  }
                }
              }}
              placeholder="Describe your care needs..."
              autoComplete="off"
              rows={1}
              className="matchmaking-textarea placeholder:text-lg w-[calc(100%-200px)] rounded-[35px] bg-white hover:bg-white focus:bg-white active:bg-white pl-6 pr-3 text-sm font-termina text-[#1F2232] placeholder:text-[#A3A7B5] focus:outline-none resize-none border-0"
              style={{
                backgroundColor: 'white',
                WebkitBoxShadow: '0 0 0 1000px white inset',
                boxShadow: '0 0 0 1000px white inset',
                paddingTop: '24px',
                paddingBottom: '24px',
                lineHeight: '22px',
                height: '70px',
                overflowY: 'hidden',
              }}
            />
          </div>
          <div className="absolute inset-y-0 right-8 flex items-center gap-3">
            <button
              type="button"
              onClick={onGenerateMatches}
              disabled={!matchingPrompt.trim() || isLoading || !canSearch}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-[#026857] text-white transition-colors disabled:opacity-60 pointer-events-auto hover:bg-[#025247]"
              aria-label="Generate matches"
            >
              <FiArrowUp className="w-6 h-6" />
            </button>
            <div className="h-6 w-px bg-[#E4E5EA]" />
            <div className="relative">
              <button
                type="button"
                onClick={() => canSearch && setHistoryPopoverOpen(!isHistoryPopoverOpen)}
                disabled={!canSearch}
                className="flex items-center justify-center pointer-events-auto disabled:cursor-not-allowed disabled:opacity-60"
                aria-label="Prompt history"
                onMouseEnter={() => canSearch && setShowHistoryTooltip(true)}
                onMouseLeave={() => setShowHistoryTooltip(false)}
              >
                <LuHistory className="w-8 h-8 text-[#026857]" />
              </button>
              {showHistoryTooltip && canSearch && (
                <div className="absolute right-0 bottom-full mb-2 pointer-events-none z-50">
                  <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-1.5 shadow-xl whitespace-nowrap text-xs font-termina capitalize text-[#111827]">
                    Prompt history
                  </div>
                </div>
              )}
              {canSearch && (
                <MatchmakingHistoryPopover
                  isOpen={isHistoryPopoverOpen}
                  onClose={() => setHistoryPopoverOpen(false)}
                  onSelectHistory={onSelectHistory}
                  advisorId={advisorId}
                  clientId={clientId}
                  shouldFetch={shouldFetchHistory && !!advisorId}
                  onFetchComplete={onFetchHistoryComplete}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

